import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, MessageBarTitle, makeStyles } from '@fluentui/react-components';
import { motion } from 'framer-motion';

import { TenantDetail } from '../../api/tenantRegistry';
import BprtStatusBadge from '../../components/BprtStatusBadge';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import RelativeTime from '../../components/RelativeTime';
import { SectionLabel } from '../../components/detailPrimitives';
import { useTenantDetailQuery } from '../../hooks/useTenantDetail';
import { ACCENT } from '../../theme/tokens';

const useStyles = makeStyles({
  tableSurface: {
    backgroundColor: '#ffffff',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    overflowX: 'auto',
  },
});

interface LinkedRow {
  key: string;
  migrationId: string;
  migrationWave: string;
  role: string | null;
  bprtStatus: string | null;
  bprtExpiresUtc: string | null;
}

// Merge linkedMigrations (role) with waveBprtStatus (BPRT lifecycle) on
// (migrationId, wave). A migration/wave may appear in one list but not the other.
function mergeRows(tenant: TenantDetail): LinkedRow[] {
  const map = new Map<string, LinkedRow>();
  const keyOf = (migrationId: string, wave: string) => `${migrationId}::${wave}`;

  for (const lm of tenant.linkedMigrations) {
    const key = keyOf(lm.migrationId, lm.migrationWave);
    map.set(key, {
      key,
      migrationId: lm.migrationId,
      migrationWave: lm.migrationWave,
      role: lm.role,
      bprtStatus: null,
      bprtExpiresUtc: null,
    });
  }
  for (const w of tenant.waveBprtStatus) {
    const key = keyOf(w.migrationId, w.migrationWave);
    const existing = map.get(key);
    if (existing) {
      existing.bprtStatus = w.bprtStatus;
      existing.bprtExpiresUtc = w.bprtExpiresUtc;
    } else {
      map.set(key, {
        key,
        migrationId: w.migrationId,
        migrationWave: w.migrationWave,
        role: null,
        bprtStatus: w.bprtStatus,
        bprtExpiresUtc: w.bprtExpiresUtc,
      });
    }
  }
  return Array.from(map.values());
}

function titleCaseRole(role: string): string {
  if (!role) return role;
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

// Linked migrations & waves tab (FD-158, ADR-023). Structural links to the
// migrations this tenant participates in, with each linked wave's BPRT status
// and expiry. Rows link out to the migration detail.
export default function TenantMigrationsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const query = useTenantDetailQuery(tenantId);
  const tenant = query.data;

  const rows = useMemo<LinkedRow[]>(() => (tenant ? mergeRows(tenant) : []), [tenant]);

  const columns = useMemo<DataTableColumn<LinkedRow>[]>(
    () => [
      {
        key: 'migrationId',
        header: 'Migration',
        render: (r) => (
          <span
            className="break-all font-mono text-xs"
            style={{ color: ACCENT.hover }}
            title={r.migrationId}
          >
            {r.migrationId}
          </span>
        ),
      },
      {
        key: 'migrationWave',
        header: 'Wave',
        render: (r) => <span className="font-display text-zinc-700">{r.migrationWave}</span>,
      },
      {
        key: 'role',
        header: 'Role',
        render: (r) =>
          r.role ? (
            <span className="font-display text-zinc-700">{titleCaseRole(r.role)}</span>
          ) : (
            <span className="text-zinc-400">—</span>
          ),
      },
      {
        key: 'bprtStatus',
        header: 'BPRT',
        render: (r) => <BprtStatusBadge status={r.bprtStatus} />,
      },
      {
        key: 'bprtExpiresUtc',
        header: 'BPRT expiry',
        numeric: true,
        render: (r) => <RelativeTime iso={r.bprtExpiresUtc} emptyPlaceholder="—" />,
      },
    ],
    [],
  );

  if (!tenant) return null;

  const emptyNode = (
    <MessageBar intent="info">
      <MessageBarBody>
        <MessageBarTitle>No linked migrations</MessageBarTitle>
        This tenant is registered but not yet linked to any migration wave.
      </MessageBarBody>
    </MessageBar>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="flex flex-col gap-5"
    >
      <div>
        <SectionLabel>Linked migrations &amp; waves</SectionLabel>
        <p className="font-display text-sm text-zinc-600 leading-relaxed max-w-[65ch] -mt-2">
          Migrations this tenant participates in, with the BPRT status and expiry for each linked
          wave. Select a row to open the migration.
        </p>
      </div>

      <div className={styles.tableSurface}>
        <DataTable
          columns={columns}
          data={rows}
          keyForRow={(r) => r.key}
          onRowClick={(r) => navigate(`/migrations/${encodeURIComponent(r.migrationId)}/overview`)}
          emptyState={emptyNode}
        />
      </div>
    </motion.div>
  );
}
